import {
  ChangeEvent,
  FC,
  FocusEvent,
  TextareaHTMLAttributes,
  useState,
} from "react";
import FieldMessage from "~/components/lib/FieldMessage";
import Label from "~/components/lib/Label";
import { toClassList } from "~/utils/classes";
import "./styles.css";

type Props = {
  id: string;
  label: string;
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: FocusEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  error?: string;
  maxLength?: number;
  required?: boolean;
  name?: string;
};

const TextArea: FC<Props> = ({
  id,
  label,
  placeholder,
  value,
  defaultValue,
  onChange,
  onBlur,
  disabled,
  error,
  maxLength,
  required,
  name,
}) => {
  const [localLength, setLocalLength] = useState(
    (value ?? defaultValue ?? "").length,
  );
  const currentLength = value !== undefined ? value.length : localLength;
  const maxLengthReached = maxLength && currentLength >= maxLength;

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (value === undefined) {
      setLocalLength(e.target.value.length);
    }
    onChange?.(e);
  };

  const className = toClassList([
    "text-area__input",
    error ? "text-area__input--error" : "",
    maxLengthReached ? "text-area__input--error" : "",
  ]);

  const inputProps: TextareaHTMLAttributes<HTMLTextAreaElement> = {
    id,
    placeholder,
    className,
    value,
    defaultValue,
    onChange: handleChange,
    onBlur,
    disabled,
    maxLength,
    required,
    name,
    "aria-invalid": !!error,
    "aria-describedby": error ? `${id}-error` : undefined,
  };

  return (
    <div className="text-area">
      <Label
        htmlFor={id}
        label={label}
      />
      <textarea {...inputProps} />
      <div className="text-area__helper-container">
        {error && (
          <FieldMessage
            id={`${id}-error`}
            type="error"
            className="text-area__error"
          >
            {error}
          </FieldMessage>
        )}
        {maxLength && (
          <FieldMessage
            id={`${id}-count`}
            className={`text-area__count ${maxLengthReached ? "text-area__count--error" : ""}`}
          >
            {currentLength}/{maxLength}
          </FieldMessage>
        )}
      </div>
    </div>
  );
};

export default TextArea;
